import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import html2pdf from "html2pdf.js";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ApiUrl } from "../../../ApiUrl";
import "./PaymentDetails.css";

const PaymentDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const passedDate = location.state?.selectedDate || "";

  const today = useMemo(() => new Date(), []);
  const formatDateKey = (dateObj) => {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const parseDateInput = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatPaymentDate = (value) => {
    if (!value) return "";

    // Keep the API date portion intact so a UTC timestamp cannot shift the day.
    const dateMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      const [, year, month, day] = dateMatch;
      return `${day}-${month}-${year}`;
    }

    const parsed = parseDateInput(value);
    return parsed
      ? parsed.toLocaleDateString("en-GB").replace(/\//g, "-")
      : String(value);
  };

  const [startDate, setStartDate] = useState(() =>
    parseDateInput(passedDate) || today
  );
  const [endDate, setEndDate] = useState(() =>
    parseDateInput(passedDate) || today
  );
  const [filteredFeeData, setFilteredFeeData] = useState([]);

  useEffect(() => {
    const parsedPassedDate = parseDateInput(passedDate);
    if (parsedPassedDate) {
      setStartDate(parsedPassedDate);
      setEndDate(parsedPassedDate);
    }
  }, [passedDate]);

  const academicSessionId = localStorage.getItem("academicSessionId");
  const orgId = localStorage.getItem("orgId");
  const branchId = localStorage.getItem("branchId");

  const fetchFeeData = (customStartDate, customEndDate) => {
    if (!academicSessionId || !orgId || !branchId) {
      setFilteredFeeData([]);
      return;
    }

    const fromDate = customStartDate ? formatDateKey(customStartDate) : "";
    const toDate = customEndDate ? formatDateKey(customEndDate) : "";

    let apiUrl = `${ApiUrl.apiurl}FeesDashBoard/feesReceiptsearch/?academic_year_id=${academicSessionId}&org_id=${orgId}&branch_id=${branchId}`;

    if (fromDate && toDate) {
      apiUrl += `&from_date=${fromDate}&to_date=${toDate}`;
    }

    fetch(apiUrl)
      .then((response) => response.json())
      .then((data) => {
        setFilteredFeeData(Array.isArray(data?.data) ? data.data : []);
      })
      .catch((error) => {
        console.error("Error fetching fee day book data:", error);
        setFilteredFeeData([]);
      });
  };

  useEffect(() => {
    fetchFeeData(startDate, endDate);
  }, [startDate, endDate, academicSessionId, orgId, branchId]);

  const handleSearchClick = () => {
    fetchFeeData(startDate, endDate);
  };

  const handleClear = () => {
    const defaultDate = parseDateInput(passedDate) || today;
    setStartDate(defaultDate);
    setEndDate(defaultDate);
    setFilteredFeeData([]);
  };

  const handleClose = () => {
    navigate("/admin/fee-dashboard");
  };

  const handlePrint = () => {
    window.print();
  };

  const exportToExcel = () => {
    if (!filteredFeeData.length) {
      alert("No data available to export.");
      return;
    }

    const ws = XLSX.utils.json_to_sheet(filteredFeeData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "FeeDetails");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "FeeDetails.xlsx");
  };

  const exportToPDF = () => {
    if (!filteredFeeData.length) {
      alert("No data available to export.");
      return;
    }

    const originalTable = document.getElementById("feeTable");
    if (!originalTable) {
      alert("Table not found. Make sure the element with ID 'feeTable' exists.");
      return;
    }

    const hiddenContainer = document.createElement("div");
    hiddenContainer.style.position = "absolute";
    hiddenContainer.style.top = "0";
    hiddenContainer.style.left = "-9999px";
    hiddenContainer.style.width = "auto";
    hiddenContainer.style.padding = "20px";
    hiddenContainer.style.background = "#fff";

    const header = document.createElement("div");
    header.style.textAlign = "center";
    header.style.marginBottom = "20px";
    header.innerHTML = `
      <h3 style="margin:0;font-weight:bold;">Fee Day Book</h3>
      <p style="margin:0;font-size:14px;">From: ${startDate ? startDate.toLocaleDateString("en-GB") : "N/A"} | To: ${endDate ? endDate.toLocaleDateString("en-GB") : "N/A"}</p>
    `;

    hiddenContainer.appendChild(header);
    hiddenContainer.appendChild(originalTable.cloneNode(true));
    document.body.appendChild(hiddenContainer);

    setTimeout(() => {
      html2canvas(hiddenContainer, { scale: 2, useCORS: true, logging: false }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const imgWidth = 210;
        const pageHeight = 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let position = 0;
        let heightLeft = imgHeight;

        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        pdf.save("FeeDetails.pdf");
        document.body.removeChild(hiddenContainer);
      });
    }, 100);
  };

  const handleReceiptLinkClick = async (receiptNo) => {
    if (!academicSessionId) {
      alert("Academic session ID not found.");
      return;
    }

    try {
      const response = await fetch(
        `${ApiUrl.apiurl}FEESRECIPTS/GetFeeReceiptsBasedOnReceiptNo/${receiptNo}/${academicSessionId}`
      );
      const result = await response.json();

      if (!response.ok) {
        alert(result.message || "Failed to fetch receipt details.");
        return;
      }

      const receipt = result.receipt_data || {};
      const receiptHtml = `
        <div id="pdf-content">
          <div style="width: 70%; border: 1px solid black; padding: 8px; font-family: Arial, sans-serif; font-size: 12px; margin: auto;">
            <div style="text-align: center; font-weight: bold; font-size: 16px;">Sparsh College of Nursing and Allied Sciences</div>
            <div style="text-align: center; font-size: 12px; margin-bottom: 8px;">Fee Receipt</div>
            <table style="width: 100%; border-collapse: collapse; border: 1px solid black; margin-top: 8px;">
              <tbody>
                <tr>
                  <td style="padding: 10px;"><strong>Receipt No:</strong></td>
                  <td style="padding: 10px;">${receipt.receipt_no || ""}</td>
                  <td style="padding: 10px;"><strong>Section:</strong></td>
                  <td style="padding: 10px;">${receipt.sectionname || ""}</td>
                </tr>
                <tr>
                  <td style="padding: 10px;"><strong>Receipt Date:</strong></td>
                  <td style="padding: 10px;">${formatPaymentDate(receipt.receipt_date)}</td>
                  <td style="padding: 10px;"><strong>Father's Name:</strong></td>
                  <td style="padding: 10px;">${receipt.fathername || ""}</td>
                </tr>
                <tr>
                  <td style="padding: 10px;"><strong>Student Name:</strong></td>
                  <td style="padding: 10px;">${receipt.studentname || ""}</td>
                  <td style="padding: 10px;"><strong>Fee Period:</strong></td>
                  <td style="padding: 10px;">${receipt.feeperiods || ""}</td>
                </tr>
                <tr>
                  <td style="padding: 10px;"><strong>Admission No:</strong></td>
                  <td style="padding: 10px;">${receipt.admission_no || ""}</td>
                  <td style="padding: 10px;"><strong>Amount:</strong></td>
                  <td style="padding: 10px;">${Number(receipt.amount || 0).toFixed(2)}</td>
                </tr>
                <tr>
                  <td style="padding: 10px;"><strong>Class:</strong></td>
                  <td style="padding: 10px;">${receipt.classname || ""}</td>
                  <td colspan="2"></td>
                </tr>
              </tbody>
            </table>
            <table style="width: 100%; border-collapse: collapse; margin-top: 8px;">
              <thead>
                <tr>
                  <th style="border: 1px solid black; padding: 4px;">Sr. No.</th>
                  <th style="border: 1px solid black; padding: 4px;">Element</th>
                  <th style="border: 1px solid black; padding: 4px;">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${Object.values(receipt.payment_element_list || {})
                  .map(
                    (element, index) => `
                      <tr>
                        <td style="border: 1px solid black; padding: 4px;">${index + 1}</td>
                        <td style="border: 1px solid black; padding: 4px;">${element.element_name || ""}</td>
                        <td style="border: 1px solid black; padding: 4px; text-align: right;">${Number(element.amount || 0).toFixed(2)}</td>
                      </tr>`
                  )
                  .join("")}
                <tr>
                  <td></td>
                  <td style="padding: 4px;"><strong>Total:</strong></td>
                  <td style="padding: 4px; text-align: right;"><strong>${Number(receipt.amount || 0).toFixed(2)}</strong></td>
                </tr>
              </tbody>
            </table>
            <table style="width: 100%; border-collapse: collapse; margin-top: 8px;">
              <thead>
                <tr>
                  <th style="border: 1px solid black; padding: 4px;">Payment Method</th>
                  <th style="border: 1px solid black; padding: 4px;">Payment Reference</th>
                  <th style="border: 1px solid black; padding: 4px;">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style="border: 1px solid black; padding: 4px;">${receipt.payment_method || ""}</td>
                  <td style="border: 1px solid black; padding: 4px;">${receipt.payment_reference || ""}</td>
                  <td style="border: 1px solid black; padding: 4px; text-align: right;">${Number(receipt.amount || 0).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
            <table style="width: 100%; border-collapse: collapse; margin-top: 8px;">
              <tbody>
                <tr>
                  <td style="padding: 4px;">Total Session Fee</td>
                  <td style="padding: 4px; text-align: right;">${Number(receipt.total_academic_year_fees || 0).toFixed(2)}</td>
                </tr>
                <tr>
                  <td style="padding: 4px;">Total Paid</td>
                  <td style="padding: 4px; text-align: right;">${Number(receipt.total_paid || 0).toFixed(2)}</td>
                </tr>
                <tr>
                  <td style="padding: 4px;">Total Balance</td>
                  <td style="padding: 4px; text-align: right;">${Number(receipt.remaining_amount || 0).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      `;

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = receiptHtml;
      document.body.appendChild(tempDiv);

      const worker = html2pdf().from(tempDiv).set({
        margin: 0.5,
        filename: `Receipt_${receipt.receipt_no || receiptNo}.pdf`,
        html2canvas: { scale: 2 },
        jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
      });

      const blob = await worker.outputPdf("blob");
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      document.body.removeChild(tempDiv);
    } catch (error) {
      console.error("Error fetching receipt details:", error);
      alert("An error occurred. Please try again.");
    }
  };

  const totalReceiptAmt = filteredFeeData.reduce(
    (total, row) => total + (parseFloat(row.receipt_amount || row.amount || 0) || 0),
    0
  );
  const totalDiscount = filteredFeeData.reduce(
    (total, row) => total + (parseFloat(row.discount || 0) || 0),
    0
  );

  const paymentMethodTotals = filteredFeeData.reduce((acc, row) => {
    const method = row.payment_method || "Unknown";
    const amount = parseFloat(row.receipt_amount || row.amount || 0) || 0;
    acc[method] = (acc[method] || 0) + amount;
    return acc;
  }, {});

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card p-0">
            <div className="card-body">
              <p
                style={{
                  marginBottom: 0,
                  textAlign: "center",
                  fontSize: "20px",
                  fontWeight: 700,
                }}
              >
                FEE DAY BOOK
              </p>

              <div className="row mb-3 mt-3 mx-0">
                <div className="col-12 d-flex flex-wrap gap-2">
                  <button className="btn btn-primary me-2" style={{ width: 150 }} onClick={handleSearchClick}>
                    Search
                  </button>
                  <button className="btn btn-primary me-2" style={{ width: 150 }} onClick={exportToExcel}>
                    Export to Excel
                  </button>
                  <button className="btn btn-primary me-2" style={{ width: 150 }} onClick={exportToPDF}>
                    Export to PDF
                  </button>
                  <button className="btn btn-danger me-2" style={{ width: 150 }} onClick={handleClose}>
                    Close
                  </button>
                  <button className="btn btn-secondary me-2" style={{ width: 150 }} onClick={handleClear}>
                    Clear
                  </button>
                  <button className="btn btn-primary me-2" style={{ width: 150 }} onClick={handlePrint}>
                    Print Receipts
                  </button>
                </div>
              </div>

              <div id="printArea">
                <div className="row mt-3 mx-1">
                  <div className="col-12 custom-section-box">
                    <div className="row flex-grow-1 mb-3">
                      <div className="col-12 col-md-4 mb-2">
                        <label htmlFor="fromDate" className="form-label">
                          From Date
                        </label>
                        <input
                          type="date"
                          id="fromDate"
                          className="form-control"
                          value={startDate ? formatDateKey(startDate) : ""}
                          onChange={(e) => setStartDate(parseDateInput(e.target.value))}
                        />
                      </div>
                      <div className="col-12 col-md-4 mb-2">
                        <label htmlFor="toDate" className="form-label">
                          To Date
                        </label>
                        <input
                          type="date"
                          id="toDate"
                          className="form-control"
                          value={endDate ? formatDateKey(endDate) : ""}
                          onChange={(e) => setEndDate(parseDateInput(e.target.value))}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div id="feeTable" className="col-12">
                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th>Sr No</th>
                          <th>Date</th>
                          <th>Receipt No</th>
                          <th>Fee Period</th>
                          <th>Name</th>
                          <th>Class</th>
                          <th>Section</th>
                          <th>School Admission No</th>
                          <th>Payment Method</th>
                          <th>Discount</th>
                          <th>Received Fees</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFeeData.map((row, index) => (
                          <React.Fragment key={row.receipt_no || index}>
                            <tr>
                              <td>{index + 1}</td>
                              <td>{formatPaymentDate(row.date || row.receipt_date)}</td>
                              <td>
                                <a
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleReceiptLinkClick(row.receipt_no || row.receiptNo || row.receipt_no);
                                  }}
                                  style={{ textDecoration: "underline", color: "blue" }}
                                >
                                  {row.receipt_no || row.receiptNo || ""}
                                </a>
                              </td>
                              <td>{row.feeappfrom || row.fee_period || row.feePeriod || ""}</td>
                              <td>{row.student_name || row.name || ""}</td>
                              <td>{row.classname || row.class || ""}</td>
                              <td>{row.sectionname || row.section || ""}</td>
                              <td>{row.admission_no || row.registration_no || ""}</td>
                              <td>{row.payment_method || ""}</td>
                              <td>{row.discount ?? 0}</td>
                              <td>{row.receipt_amount ?? row.amount ?? 0}</td>
                            </tr>
                          </React.Fragment>
                        ))}

                        <tr>
                          <td colSpan="11">
                            <div className="row">
                              <div className="col-md-6">
                                <table className="table table-bordered mb-0">
                                  <thead>
                                    <tr>
                                      <th>Payment Method</th>
                                      <th>Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {Object.entries(paymentMethodTotals).map(([method, amount]) => (
                                      <tr key={method}>
                                        <td>{method}</td>
                                        <td>{amount.toFixed(2)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              <div className="col-md-6">
                                <table className="table mb-0">
                                  <tbody>
                                    <tr>
                                      <td className="text-end fw-bold">Total Receipt Amt.:</td>
                                      <td className="fw-bold">{totalReceiptAmt.toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                      <td className="text-end fw-bold">Total Discount:</td>
                                      <td className="fw-bold">{totalDiscount.toFixed(2)}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetails;
